import { useCallback, useLayoutEffect, useRef, useState } from 'react'
import { cn } from '@/common/lib/cn/cn'
import { Icon, type IconName } from '@/common/ui/Icon'

const FADE_DISTANCE = 24
const SCROLL_GRADIENT_FROM = '#323238'

export type ChoiceGroupItem = {
    label?: string
    iconName?: IconName
}

export type ChoiceGroupProps = {
    className?: string
    activeIndex?: number
    items: Array<string | ChoiceGroupItem>
    onChange?: (index: number) => void
}

export const ChoiceGroup = ({ className, activeIndex = 0, items, onChange }: ChoiceGroupProps) => {
    const scrollRef = useRef<HTMLDivElement | null>(null)
    const containerRef = useRef<HTMLDivElement | null>(null)
    const itemRefs = useRef<Array<HTMLButtonElement | null>>([])
    const [indicatorStyle, setIndicatorStyle] = useState<{ width: number; transform: string } | null>(null)
    const [leftGradientOpacity, setLeftGradientOpacity] = useState(0)
    const [rightGradientOpacity, setRightGradientOpacity] = useState(0)

    const updateScrollState = useCallback(() => {
        const container = containerRef.current
        const scrollContainer = scrollRef.current
        const activeItem = itemRefs.current[activeIndex]

        if (container && activeItem) {
            const containerRect = container.getBoundingClientRect()
            const itemRect = activeItem.getBoundingClientRect()

            setIndicatorStyle({
                width: itemRect.width,
                transform: `translateX(${itemRect.left - containerRect.left}px)`,
            })
        }

        if (scrollContainer) {
            const distanceToRight = Math.max(
                scrollContainer.scrollWidth - scrollContainer.clientWidth - scrollContainer.scrollLeft,
                0
            )
            const leftFadeRatio = Math.min(scrollContainer.scrollLeft / FADE_DISTANCE, 1)
            const rightFadeRatio = Math.min(distanceToRight / FADE_DISTANCE, 1)

            setLeftGradientOpacity(leftFadeRatio)
            setRightGradientOpacity(rightFadeRatio)
        }
    }, [activeIndex])

    const scrollActiveItemIntoView = useCallback(() => {
        const scrollContainer = scrollRef.current
        const activeItem = itemRefs.current[activeIndex]
        if (!scrollContainer || !activeItem) return

        const containerRect = scrollContainer.getBoundingClientRect()
        const itemRect = activeItem.getBoundingClientRect()

        if (itemRect.left < containerRect.left) {
            scrollContainer.scrollLeft -= containerRect.left - itemRect.left
            return
        }

        if (itemRect.right > containerRect.right) {
            scrollContainer.scrollLeft += itemRect.right - containerRect.right
        }
    }, [activeIndex])

    useLayoutEffect(() => {
        scrollActiveItemIntoView()
        updateScrollState()
    }, [scrollActiveItemIntoView, updateScrollState, items])

    useLayoutEffect(() => {
        const scrollContainer = scrollRef.current
        if (!scrollContainer) return

        const resizeObserver = new ResizeObserver(updateScrollState)
        resizeObserver.observe(scrollContainer)

        if (containerRef.current) {
            resizeObserver.observe(containerRef.current)
        }

        scrollContainer.addEventListener('scroll', updateScrollState, { passive: true })
        window.addEventListener('resize', updateScrollState)

        return () => {
            resizeObserver.disconnect()
            scrollContainer.removeEventListener('scroll', updateScrollState)
            window.removeEventListener('resize', updateScrollState)
        }
    }, [updateScrollState])

    return (
        <div className={cn('min-w-0 w-fit max-w-full', className)}>
            <div className='relative min-w-0 w-full overflow-hidden rounded-[10px]'>
                <div
                    ref={scrollRef}
                    className='min-w-0 w-full overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
                >
                    <div
                        ref={containerRef}
                        className='relative flex w-max items-center rounded-[10px] bg-[rgba(255,255,255,0.05)]'
                    >
                        {indicatorStyle && (
                            <span
                                className='absolute left-0 top-0 h-full rounded-[10px] bg-[rgba(255,255,255,0.1)] transition-transform duration-200 ease-out'
                                style={{ width: indicatorStyle.width, transform: indicatorStyle.transform }}
                            />
                        )}
                        {items.map((item, index) => {
                            const normalizedItem: ChoiceGroupItem = typeof item === 'string' ? { label: item } : item
                            const isActive = index === activeIndex
                            return (
                                <button
                                    key={`${normalizedItem.label}-${index}`}
                                    type='button'
                                    className={cn(
                                        'relative z-[1] flex shrink-0 cursor-pointer items-center justify-center gap-[8px] overflow-clip whitespace-nowrap px-[12px] py-3 transition-colors'
                                    )}
                                    onClick={() => onChange?.(index)}
                                    aria-pressed={isActive}
                                    ref={element => {
                                        itemRefs.current[index] = element
                                    }}
                                >
                                    {normalizedItem.iconName && <Icon name={normalizedItem.iconName} size='sm' />}
                                    {normalizedItem.label && (
                                        <p
                                            className={cn(
                                                'text-[16px] leading-[16px] font-normal',
                                                isActive ? 'text-white' : 'text-[#aba7af]'
                                            )}
                                        >
                                            {normalizedItem.label}
                                        </p>
                                    )}
                                </button>
                            )
                        })}
                    </div>
                </div>

                <div
                    className='pointer-events-none absolute left-0 top-0 z-[2] h-full w-[24px]'
                    style={{
                        opacity: leftGradientOpacity,
                        backgroundImage: `linear-gradient(to right, ${SCROLL_GRADIENT_FROM}, transparent)`,
                    }}
                    aria-hidden='true'
                />

                <div
                    className='pointer-events-none absolute right-0 top-0 z-[2] h-full w-[24px]'
                    style={{
                        opacity: rightGradientOpacity,
                        backgroundImage: `linear-gradient(to left, ${SCROLL_GRADIENT_FROM}, transparent)`,
                    }}
                    aria-hidden='true'
                />
            </div>
        </div>
    )
}
